import React from 'react';
import { devLog, devWarn, devError } from '../../../utils/devLogger';
import ImageView from '../../../components/ImageView';
import { LotData } from './AuctionDetailPopup';
import {
  LeftSection,
  LotsTitle,
  ProgressContainer,
  ProgressLabel,
  ProgressBar,
  ProgressFill,
  LotsContainer,
  LotCard,
  LotCardImage,
  LotCardContent,
  LotCardHeader,
  LotCardChip,
  LotCardStatus,
  LotCardName,
  LotCardPrice,
  LotCardTime
} from './AuctionDetailStyles';

interface AuctionLotsListProps {
  lots: LotData[];
  selectedLotId: string;
  auctionStartAt?: string; // 경매 시작 시간 추가
  auctionEndAt?: string; // 경매 종료 시간 추가
  onLotSelect: (lotId: string) => void;
}

const AuctionLotsList: React.FC<AuctionLotsListProps> = ({
  lots,
  selectedLotId,
  auctionStartAt,
  auctionEndAt,
  onLotSelect
}) => {
  // 라이브 경매가 시작되기 전인지 확인
  const isBeforeAuctionStart = auctionStartAt ? new Date() < new Date(auctionStartAt) : false;
  
  // 경매가 종료되었는지 확인
  const isAfterAuctionEnd = auctionEndAt ? new Date() > new Date(auctionEndAt) : false;
  
  // 개발 모드에서 상태 확인용 로그
  const isDebugMode = process.env.NODE_ENV === 'development' || 
                     window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';
  
  if (isDebugMode) {
    devLog('📋 [AuctionLotsList] 상태 확인:', {
      auctionStartAt,
      auctionEndAt,
      isBeforeAuctionStart,
      isAfterAuctionEnd,
      currentTime: new Date().toISOString(),
      totalLots: lots.length
    });
  }
  // 이미지 URL 처리 함수 (LiveSection과 동일)
  const getProfileImageSrc = (profileImageUrl?: string | null) => {
    if (!profileImageUrl || profileImageUrl.trim() === "") {
      return "/common/nodata.svg";
    }
    
    // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
    if (profileImageUrl.startsWith("http") || profileImageUrl.startsWith("blob:")) {
      return profileImageUrl;
    }
    
    // 절대 경로인 경우 (/로 시작)
    if (profileImageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${profileImageUrl}`;
    }
    
    // 단순 파일명인 경우 (서버에서 받은 파일명)
    return `${process.env.REACT_APP_API_HOST}/${profileImageUrl}`;
  };

  // LOT의 첫 번째 이미지를 가져오는 함수
  const getFirstImageUrl = (lot: LotData): string => {
    // images 배열이 있는 경우 (소켓 데이터)
    if ((lot as any).images && Array.isArray((lot as any).images) && (lot as any).images.length > 0) {
      return getProfileImageSrc((lot as any).images[0]);
    }
    // thumbnail이 있는 경우 (기존 데이터)
    else if (lot.thumbnail && lot.thumbnail.trim() !== "") {
      return getProfileImageSrc(lot.thumbnail);
    }
    // 기본 이미지
    return "/common/nodata.svg";
  };
  // 가격 포맷팅 함수
  const formatPrice = (price: number, currency: 'KRW' | 'USD') => {
    if (currency === 'KRW') {
      return `₩${price.toLocaleString()}`;
    } else {
      return `$${price.toLocaleString()}`;
    }
  };

  const getStatusText = (status: string) => {
    // 경매가 종료된 후라면 모든 상태를 '경매종료'로 표시
    if (isAfterAuctionEnd) {
      return '경매종료';
    }
    
    // 라이브 경매 시작 전이면 모든 상태를 '진행예정'으로 표시
    if (isBeforeAuctionStart) {
      return '진행예정';
    }
    
    switch (status) {
      case 'ONGOING': return '진행 중';
      case 'WAITING': return '대기';
      case 'COMPLETED': return '경매종료';
      default: return '대기';
    }
  };

  const getPriceInfo = (lot: LotData) => {
    switch (lot.status) {
      case 'COMPLETED':
        return `Final: ${formatPrice(lot.currentPriceKrw, 'KRW')}`;
      case 'ONGOING':
        const bidCount = lot.bidHistory.length;
        return `${formatPrice(lot.currentPriceKrw, 'KRW')} (${bidCount} bids)`;
      case 'WAITING':
        return `Starting: ${formatPrice(lot.startPriceKrw, 'KRW')}`;
      default:
        return `Starting: ${formatPrice(lot.startPriceKrw, 'KRW')}`;
    }
  };

  const getTimeInfo = (lot: LotData) => {
    switch (lot.status) {
      case 'ONGOING':
        return 'Ends in 2m 30s';
      default:
        return '';
    }
  };

  const completedLots = lots.filter(lot => lot.status === 'COMPLETED').length;
  const totalLots = lots.length;
  
  // 경매 종료 후라면 진행률 100%, 라이브 경매 시작 전이면 진행률 0%, 아니면 정상 계산
  const progressPercentage = isAfterAuctionEnd ? 100 : 
                            isBeforeAuctionStart ? 0 : 
                            (totalLots > 0 ? (completedLots / totalLots) * 100 : 0);

  return (
    <LeftSection>
      <LotsTitle>경매 Lots</LotsTitle>
      
      {/* 프로세스 바 */}
      <ProgressContainer>
        <ProgressLabel>
          {isAfterAuctionEnd 
            ? `경매 종료 - ${totalLots}개 LOT 완료`
            : isBeforeAuctionStart 
              ? `${totalLots}개 LOT 진행예정` 
              : `${totalLots}개 LOT 중 ${totalLots - completedLots}개 남음`}
        </ProgressLabel>
        <ProgressBar>
          <ProgressFill percentage={progressPercentage} />
        </ProgressBar>
      </ProgressContainer>

      {/* LOT 카드들 */}
      <LotsContainer>
        {lots.map((lot) => (
          <LotCard 
            key={lot.lotId} 
            isSelected={lot.lotId === selectedLotId}
            status={lot.status}
            onClick={() => onLotSelect(lot.lotId)}
          >
            <LotCardImage>
              <ImageView
                imageUrl={getFirstImageUrl(lot)}
                width={80}
                height={80}
                borderRadius={8}
                alt={lot.lotName}
                objectFit="cover"
              />
            </LotCardImage>
            <LotCardContent>
              <LotCardHeader>
                <LotCardChip>{lot.lotId}</LotCardChip>
                <LotCardStatus status={isAfterAuctionEnd ? 'COMPLETED' : isBeforeAuctionStart ? 'SCHEDULED' : lot.status}>
                  {getStatusText(lot.status)}
                </LotCardStatus>
              </LotCardHeader>
              <LotCardName>{lot.lotName}</LotCardName>
              <LotCardPrice>{getPriceInfo(lot)}</LotCardPrice>
              {getTimeInfo(lot) && <LotCardTime>{getTimeInfo(lot)}</LotCardTime>}
            </LotCardContent>
          </LotCard>
        ))}
      </LotsContainer>
    </LeftSection>
  );
};

export default AuctionLotsList;
